import type { ChartData } from '@/types/chart';

function normalizeText(value: unknown): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function normalizeChartType(type: ChartData['type'] | string | undefined): ChartData['type'] {
  const text = normalizeText(type).replace(/[_-\s]+/g, '');
  if (text === 'radial' || text === 'spider') return 'radar';
  if (text === 'donut' || text === 'dona') return 'doughnut';
  if (text === 'radar') return 'radar';
  if (text === 'scatter') return 'scatter';
  if (text === 'line') return 'line';
  if (text === 'pie') return 'pie';
  if (text === 'doughnut') return 'doughnut';
  return 'bar';
}

function canonicalAxisLabel(value: string): string {
  const text = normalizeText(value);
  if (!text) return '';
  if (text.includes('distancia') || text.includes('recorrido') || text.includes('kilomet')) {
    return 'Distancia restante (km)';
  }
  if (text.includes('riesgo') || text.includes('porcentaje') || text.includes('theft') || text.includes('risk')) {
    return 'Porcentaje de riesgo (%)';
  }
  if (text.includes('velocidad') || text.includes('km/h') || text.includes('speed')) {
    return 'Velocidad promedio (km/h)';
  }
  if (text.includes('eta') || text.includes('minuto') || /\bmin\b/.test(text)) {
    return 'ETA promedio (min)';
  }
  return value.trim();
}

function parseScatterAxisLabels(chart: ChartData): { x?: string; y?: string } {
  const sources = [
    chart.original_instruction,
    chart.title,
    chart.dataset_label,
  ].filter(Boolean) as string[];

  for (const source of sources) {
    const display = source.trim();
    const vsMatch = display.match(/(?:dispersi[oó]n(?:\s+operativa)?(?:\s+de)?\s+)?(.+?)\s+vs\.?\s+(.+)/i);
    if (vsMatch) {
      return {
        x: canonicalAxisLabel(vsMatch[1]),
        y: canonicalAxisLabel(vsMatch[2]),
      };
    }

    const contraMatch = display.match(/(?:dispersi[oó]n(?:\s+operativa)?(?:\s+de)?\s+)?(.+?)\s+contra\s+(?:la|el|los|las)?\s*(.+)/i);
    if (contraMatch) {
      return {
        x: canonicalAxisLabel(contraMatch[2]),
        y: canonicalAxisLabel(contraMatch[1]),
      };
    }
  }

  return {};
}

function enrichScatterChart(chart: ChartData): ChartData {
  if (normalizeChartType(chart.type) !== 'scatter') {
    return chart;
  }

  if (chart.x_label && chart.y_label) {
    return chart;
  }

  const parsed = parseScatterAxisLabels(chart);
  return {
    ...chart,
    x_label: chart.x_label || parsed.x,
    y_label: chart.y_label || parsed.y,
  };
}

export function normalizeChartPayload(chart: ChartData): ChartData {
  const normalized = {
    ...chart,
    type: normalizeChartType(chart.type),
  } satisfies ChartData;

  return enrichScatterChart(normalized);
}
